/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import api from '@tenant/core/api/main/api';
import Service from '../../api/Service';
import ValidationError from '../../Errors/ValidationError';
import UnauthorizedError from '../../Errors/UnauthorizedError';
import TwoFactorRequiredError from '../../Errors/TwoFactorRequiredError';
import {
  ENDPOINT_AUTH_LOGIN,
  ENDPOINT_AUTH_TWO_FACTOR_RESEND,
  ENDPOINT_AUTH_TWO_FACTOR_VERIFY,
  KEY_AUTHENTICATED,
  KEY_AUTHENTICATION_TOKEN,
} from '../../utils/constants';
import {
  setCookie,
  deleteCookie,
  getCookie,
  forgetTwoFactorDeviceToken,
  getTwoFactorDeviceToken,
  setTwoFactorDeviceToken,
} from '../../utils/helpers';

/**
 * Login and two-factor verify return the same payload, so they share a shape.
 */
const sessionFromResponse = response => ({
  token: response.access_token,
  error: false,
  pending: false,
  has_security_question: response.has_security_question,
  roles: response.roles,
  permissions: response.permissions,
  verified: response.verified,
  username: response.username || null,
  two_factor_enrolment_required: Boolean(
    response.two_factor_enrolment_required
  ),
  two_factor_channels: response.two_factor_channels || [],
});

/**
 * getErrors() throws if there are no field errors to flatten, so check first.
 */
const errorsFrom = error => {
  if (typeof error.getErrors === 'function' && error.errors) {
    return error.getErrors();
  }
  return error.errors || {};
};

const rejectionFromError = error => ({
  message: error.message,
  code: error.getCode && error.getCode(),
  errors: errorsFrom(error),
});

export const checkTokenAsync = createAsyncThunk(
  'users/checkTokenAsync',
  async (_, thunkAPI) => {
    try {
      const response = await Service.get('auth/user');
      return {
        token: true,
        verified: response.verified,
        roles: response.roles,
        permissions: response.permissions,
        email: response.email,
        username: response.username,
        pending: false,
        has_security_question: response.has_security_question,
        known_as: response.profile.known_as,
      };
    } catch (error) {
      if (
        error instanceof ValidationError ||
        error instanceof UnauthorizedError
      ) {
        // 422, 403, 401
        return thunkAPI.rejectWithValue({
          error: {
            message: error.message,
          },
          token: error.getCode() === 403 ? true : null,
          verified: error.getCode() === 403 ? false : undefined,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loginAsync = createAsyncThunk(
  'users/loginAsync',
  async ({password, username}, thunkAPI) => {
    const deviceToken = getTwoFactorDeviceToken();
    try {
      const response = await Service.post(ENDPOINT_AUTH_LOGIN, {
        password,
        username,
        ...(deviceToken ? {device_token: deviceToken} : {}),
      });
      if (response.access_token === undefined) {
        return thunkAPI.rejectWithValue({
          error: {message: 'Please check your username or email & password'},
        });
      }
      // Clear any cached responses from a previous session so the newly
      // logged-in account never inherits the last account's data.
      thunkAPI.dispatch(api.util.resetApiState());
      return sessionFromResponse(response);
    } catch (error) {
      if (error instanceof TwoFactorRequiredError) {
        if (deviceToken) {
          forgetTwoFactorDeviceToken();
        }
        return {twoFactor: error.getChallenge()};
      }
      return thunkAPI.rejectWithValue(rejectionFromError(error));
    }
  }
);

export const verifyTwoFactorAsync = createAsyncThunk(
  'users/verifyTwoFactorAsync',
  async ({challenge, code, trustDevice, useRecoveryCode}, thunkAPI) => {
    try {
      const response = await Service.post(ENDPOINT_AUTH_TWO_FACTOR_VERIFY, {
        challenge,
        code,
        trust_device: Boolean(trustDevice),
        // routes the value to the single-use recovery-code store, for a tenant
        // who cannot reach their usual second step
        use_recovery_code: Boolean(useRecoveryCode),
      });
      if (response.access_token === undefined) {
        return thunkAPI.rejectWithValue({
          errors: {
            code: 'We could not verify that code. Please log in and try again.',
          },
        });
      }
      if (response.device_token) {
        setTwoFactorDeviceToken(response.device_token);
      }
      thunkAPI.dispatch(api.util.resetApiState());
      return sessionFromResponse(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(rejectionFromError(error));
    }
  }
);

export const resendTwoFactorAsync = createAsyncThunk(
  'users/resendTwoFactorAsync',
  async ({challenge, channel}, thunkAPI) => {
    try {
      const response = await Service.post(ENDPOINT_AUTH_TWO_FACTOR_RESEND, {
        challenge,
        ...(channel ? {channel} : {}),
      });
      return {
        ...(response.challenge ? {challenge: response.challenge} : {}),
        channel: response.channel,
        destination: response.destination ?? null,
        expires_at: response.expires_at ?? null,
        can_use_email: response.can_use_email ?? false,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(rejectionFromError(error));
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'users/logoutAsync',
  async (_, thunkAPI) => {
    try {
      await Service.get('auth/logout');
      return {
        email: null,
        error: false,
        has_security_question: false,
        known_as: '',
        pending: false,
        permissions: [],
        roles: [],
        token: null,
        username: null,
        verified: undefined,
        registered: false,
        twoFactor: null,
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error: {message: 'Unexpected error occured.'},
        });
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.dispatch(api.util.resetApiState());
    }
  }
);

export const deleteProfileAsync = createAsyncThunk(
  'users/deleteProfileAsync',
  async (_, thunkAPI) => {
    try {
      await Service.delete('tenant', {data: {confirmed_delete: 1}});
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error: {message: 'Unexpected error occured.'},
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Applies a token payload to the store and the session cookies.
 */
const authenticate = (state, payload) => {
  state.pending = payload.pending;
  state.error = payload.error;
  state.token = payload.token;
  state.roles = payload.roles;
  state.permissions = payload.permissions;
  state.has_security_question = payload.has_security_question;
  state.verified = payload.verified;
  state.username = payload.username;
  state.twoFactor = null;
  state.two_factor_enrolment_required = Boolean(
    payload.two_factor_enrolment_required
  );
  state.two_factor_channels = payload.two_factor_channels || [];
  setCookie(KEY_AUTHENTICATION_TOKEN, payload.token);
  setCookie(KEY_AUTHENTICATED, 'true');
};

export const userSlice = createSlice({
  name: 'user',
  initialState: {
    email: null,
    error: false,
    error_code: -1,
    has_security_question: false,
    known_as: '',
    pending: true,
    permissions: [],
    roles: [],
    token: null,
    username: null,
    verified: undefined,
    registered: false,
    twoFactor: null,
    two_factor_enrolment_required: false,
    two_factor_channels: [],
  },
  reducers: {
    setError: (state, {payload}) => {
      state.error = payload;
    },
    setRegistered: (state, {payload}) => {
      state.registered = payload;
    },
    clearTwoFactor: state => {
      state.twoFactor = null;
    },
    clearTwoFactorEnrolment: state => {
      state.two_factor_enrolment_required = false;
      state.two_factor_channels = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(checkTokenAsync.fulfilled, (state, {payload}) => {
        state.pending = false;
        state.error = false;
        state.token = getCookie(KEY_AUTHENTICATION_TOKEN);
        state.known_as = payload.known_as;
        state.roles = payload.roles;
        state.permissions = payload.permissions;
        state.email = payload.email;
        state.username = payload.username;
        state.verified = payload.verified;
        state.has_security_question = payload.has_security_question;
      })
      .addCase(checkTokenAsync.pending, state => {
        state.pending = true;
        state.error = false;
      })
      .addCase(checkTokenAsync.rejected, (state, {payload}) => {
        state.pending = false;
        state.error = false;
        state.verified = payload.verified;
        state.token = payload.token;
        if (payload.verified !== false) {
          deleteCookie(KEY_AUTHENTICATION_TOKEN);
          deleteCookie(KEY_AUTHENTICATED);
        }
      })
      .addCase(loginAsync.fulfilled, (state, {payload}) => {
        if (payload.twoFactor) {
          state.pending = false;
          state.error = false;
          state.token = null;
          state.twoFactor = payload.twoFactor;
          return;
        }
        authenticate(state, payload);
      })
      .addCase(loginAsync.pending, state => {
        state.pending = true;
        state.error = false;
        state.twoFactor = null;
      })
      .addCase(loginAsync.rejected, (state, {payload = {}}) => {
        state.pending = false;
        state.error = payload.error;
        state.verified = false;
        state.twoFactor = null;
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
      })
      .addCase(verifyTwoFactorAsync.fulfilled, (state, {payload}) => {
        authenticate(state, payload);
      })
      .addCase(verifyTwoFactorAsync.pending, state => {
        state.error = false;
      })
      .addCase(verifyTwoFactorAsync.rejected, state => {
        state.pending = false;
      })
      .addCase(resendTwoFactorAsync.fulfilled, (state, {payload}) => {
        state.twoFactor = {...state.twoFactor, ...payload};
      })
      .addCase(deleteProfileAsync.pending, state => {
        state.pending = true;
        state.error = false;
      })
      .addCase(deleteProfileAsync.rejected, (state, {payload}) => {
        state.pending = false;
        state.error = payload.error;
      })
      .addCase(deleteProfileAsync.fulfilled, (state, {payload}) => {
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
        return {pending: false, error: false, ...payload};
      })
      .addCase(logoutAsync.fulfilled, (state, {payload}) => {
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
        return {pending: false, error: false, ...payload};
      })
      .addCase(logoutAsync.rejected, state => {
        state.pending = false;
        state.error = true;
        deleteCookie(KEY_AUTHENTICATION_TOKEN);
        deleteCookie(KEY_AUTHENTICATED);
      });
  },
});

// export const {setPending} = userSlice.actions;

export const userState = state => state.user;

export const {
  setError,
  setRegistered,
  clearTwoFactor,
  clearTwoFactorEnrolment,
} = userSlice.actions;

export default userSlice.reducer;
